/**
 * Order hooks.
 *   useMyOrders — teacher's history; refetches when the tab regains focus.
 *   useOrder    — single order; live status via Socket.io with a refetch on
 *                 tab visibility for the case where a socket event was
 *                 missed (network blip, phone backgrounded, etc.)
 *
 * Both guard against out-of-order responses (a slow earlier fetch overwriting
 * a fresher later one) and setState-after-unmount, via a monotonic request
 * sequence plus a mounted/cancelled flag.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Order, OrderStatusChangePayload } from '@cracked-mug/shared';
import { fetchMyOrders, fetchOrder } from '../services/ordersApi';
import { getSocket } from '../services/socket';

const TERMINAL_STATUSES = new Set<Order['status']>(['completed', 'cancelled']);

export function useMyOrders(): {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const seqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const seq = ++seqRef.current;
    setIsLoading(true);
    try {
      const data = await fetchMyOrders();
      // Drop the result if unmounted or superseded by a newer refresh.
      if (!mountedRef.current || seq !== seqRef.current) return;
      setOrders(data);
      setError(null);
    } catch (err) {
      if (!mountedRef.current || seq !== seqRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      if (mountedRef.current && seq === seqRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Pull fresh history when the teacher returns to the tab — a backgrounded
    // order may have completed in the meantime.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refresh]);

  return { orders, isLoading, error, refresh };
}

export function useOrder(orderId: string | undefined): {
  order: Order | null;
  isLoading: boolean;
  error: string | null;
} {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mirror the latest order into a ref so the visibilitychange handler can
  // read the current status (for its terminal-state short-circuit) without
  // re-subscribing the whole effect every time the order changes.
  const orderRef = useRef<Order | null>(null);
  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  useEffect(() => {
    if (orderId === undefined) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    // Monotonic token so overlapping fetches (a socket event + a visibility
    // refetch firing together) apply only the newest response.
    let seq = 0;

    const fetchOnce = async (): Promise<void> => {
      const mine = ++seq;
      try {
        const data = await fetchOrder(orderId);
        if (cancelled || mine !== seq) return;
        setOrder(data);
        setError(null);
      } catch (err) {
        if (cancelled || mine !== seq) return;
        setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        if (!cancelled && mine === seq) setIsLoading(false);
      }
    };

    void fetchOnce();

    const socket = getSocket();

    // Server emits `order:status_change` for every transition. We refetch
    // on a match so the UI reflects the full updated order (status +
    // acceptedAt/readyAt/etc.) without us reconstructing it client-side.
    const handleStatusChange = (payload: OrderStatusChangePayload): void => {
      if (payload.orderId !== orderId) return;
      void fetchOnce();
    };
    socket.on('order:status_change', handleStatusChange);

    // Belt-and-suspenders: if the tab was backgrounded or the socket
    // missed an event while the device slept, pull fresh state on return
    // (unless the order is already in a terminal state).
    const handleVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      const current = orderRef.current;
      if (current !== null && TERMINAL_STATUSES.has(current.status)) return;
      void fetchOnce();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      socket.off('order:status_change', handleStatusChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [orderId]);

  return { order, isLoading, error };
}
